import type { AiCapabilities } from "./types";

export const PHOTO_AI_TRANSFORMERS_CACHE_KEY = "reos-transformers-photo-ai-v1";
export const PHOTO_AI_MODEL_ID = "onnx-community/BEN2-ONNX";
export const PHOTO_AI_MODEL_DISPLAY_NAME = "BEN2 product matting";
export const PHOTO_AI_MODEL_ESTIMATED_BYTES = 219_121_675;

const PHOTO_AI_MODEL_SETUP_KEY = "reos.photoAi.localModelSetup.v1";
const PHOTO_AI_MODEL_REVISION = "main";
const HUGGING_FACE_HOST = "https://huggingface.co";
const PHOTO_AI_MODEL_FILES = ["config.json", "preprocessor_config.json", "onnx/model_fp16.onnx"] as const;

interface StoredModelSetup {
  acceptedAt?: string;
  preparedAt?: string;
  modelID?: string;
  modelName?: string;
  cacheKey?: string;
}

export interface PhotoAiModelStorageState {
  cacheSupported: boolean;
  storageSupported: boolean;
  persisted?: boolean;
  usageBytes?: number;
  quotaBytes?: number;
  cachedFiles: number;
  totalFiles: number;
  modelCached: boolean;
  acceptedAt?: string;
  preparedAt?: string;
}

export function formatStorageSize(bytes: number) {
  if (bytes >= 1_000_000_000) {
    return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  }
  return `${Math.round(bytes / 1_000_000)} MB`;
}

export async function inspectPhotoAiModelStorage(): Promise<PhotoAiModelStorageState> {
  const setup = readStoredModelSetup();
  const cachedFiles = await countCachedModelFiles();
  const storage = typeof navigator !== "undefined" ? navigator.storage : undefined;
  const estimate = storage?.estimate ? await storage.estimate().catch(() => undefined) : undefined;
  const persisted = storage?.persisted ? await storage.persisted().catch(() => undefined) : undefined;

  return {
    cacheSupported: typeof caches !== "undefined",
    storageSupported: Boolean(storage),
    persisted,
    usageBytes: estimate?.usage,
    quotaBytes: estimate?.quota,
    cachedFiles,
    totalFiles: PHOTO_AI_MODEL_FILES.length,
    modelCached: cachedFiles === PHOTO_AI_MODEL_FILES.length,
    acceptedAt: setup.acceptedAt,
    preparedAt: setup.preparedAt
  };
}

export async function requestPhotoAiPersistentStorage() {
  const storage = typeof navigator !== "undefined" ? navigator.storage : undefined;
  if (!storage?.persist) {
    return false;
  }
  return storage.persist().catch(() => false);
}

export function markPhotoAiModelAccepted() {
  writeStoredModelSetup({
    ...readStoredModelSetup(),
    acceptedAt: readStoredModelSetup().acceptedAt ?? new Date().toISOString(),
    modelID: PHOTO_AI_MODEL_ID,
    modelName: PHOTO_AI_MODEL_DISPLAY_NAME,
    cacheKey: PHOTO_AI_TRANSFORMERS_CACHE_KEY
  });
}

export function markPhotoAiModelPrepared(capabilities?: AiCapabilities) {
  writeStoredModelSetup({
    ...readStoredModelSetup(),
    acceptedAt: readStoredModelSetup().acceptedAt ?? new Date().toISOString(),
    preparedAt: new Date().toISOString(),
    modelID: capabilities?.modelID ?? PHOTO_AI_MODEL_ID,
    modelName: capabilities?.modelName ?? PHOTO_AI_MODEL_DISPLAY_NAME,
    cacheKey: PHOTO_AI_TRANSFORMERS_CACHE_KEY
  });
}

export async function clearPhotoAiModelStorage() {
  if (typeof caches !== "undefined") {
    const cache = await caches.open(PHOTO_AI_TRANSFORMERS_CACHE_KEY).catch(() => undefined);
    if (cache) {
      const requests = await cache.keys();
      await Promise.all(
        requests
          .filter((request) => isPhotoAiModelCacheURL(request.url))
          .map((request) => cache.delete(request))
      );
    }
  }

  try {
    localStorage.removeItem(PHOTO_AI_MODEL_SETUP_KEY);
  } catch {
    // Storage access can be unavailable in private or restricted contexts.
  }
}

function readStoredModelSetup(): StoredModelSetup {
  try {
    const raw = localStorage.getItem(PHOTO_AI_MODEL_SETUP_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as StoredModelSetup;
    return parsed.cacheKey === PHOTO_AI_TRANSFORMERS_CACHE_KEY ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredModelSetup(setup: StoredModelSetup) {
  try {
    localStorage.setItem(PHOTO_AI_MODEL_SETUP_KEY, JSON.stringify(setup));
  } catch {
    // The model can still run from memory if durable storage is unavailable.
  }
}

async function countCachedModelFiles() {
  if (typeof caches === "undefined") {
    return 0;
  }

  const cache = await caches.open(PHOTO_AI_TRANSFORMERS_CACHE_KEY).catch(() => undefined);
  if (!cache) {
    return 0;
  }

  const requests = await cache.keys();
  const cachedUrls = requests.map((request) => request.url);
  return photoAiModelCacheUrls().filter((url) => cachedUrls.includes(url)).length;
}

function isPhotoAiModelCacheURL(url: string) {
  return photoAiModelCacheUrls().includes(url);
}

function photoAiModelCacheUrls() {
  return PHOTO_AI_MODEL_FILES.map((file) => `${HUGGING_FACE_HOST}/${PHOTO_AI_MODEL_ID}/resolve/${PHOTO_AI_MODEL_REVISION}/${file}`);
}
